import { Link, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { BlogPost } from '../../types/blog';
import LandingFooter from '../ui/LandingFooter';
import LandingHeader from '../ui/LandingHeader';
import { SEO } from '../SEO';
import { getBlogPosts } from '../../services/blogService';
import { router } from '../../router';

const POSTS_PER_PAGE = 12;

type SortOption = 'newest' | 'oldest' | 'title-asc' | 'title-desc';

export default function BlogPage() {
  const search = useSearch({
    from: '/blog'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  // Get filter values from URL using TanStack Router search params
  const categoryFilters = search.category ?
    (Array.isArray(search.category) ? search.category : [search.category]) :
    [];
  const tagFilter = search.tag as string || '';

  useEffect(() => {
    async function loadPosts() {
      setLoading(true);
      const allPosts = await getBlogPosts();
      setPosts(allPosts);
      setLoading(false);
    }

    loadPosts();
  }, []);

  // Get all categories
  const categories = Array.from(new Set(posts.map(post => post.category)));

  // Filter and sort posts
  const filteredPosts = posts.filter(post =>
    (searchQuery
      ? post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase())
      : true) &&
    (categoryFilters.length > 0 ? categoryFilters.includes(post.category.toLowerCase()) : true) &&
    (tagFilter ? post.tags.some(tag => tag.toLowerCase() === tagFilter.toLowerCase()) : true)
  );

  // Sort posts
  filteredPosts.sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      case 'oldest':
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      case 'title-asc':
        return a.title.localeCompare(b.title);
      case 'title-desc':
        return b.title.localeCompare(a.title);
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);

  // Handle filter changes with proper TanStack Router navigation
  const handleCategoryClick = (category: string) => {
    const categoryLower = category.toLowerCase();
    const currentCategories = [...categoryFilters];

    if (currentCategories.includes(categoryLower)) {
      const newCategories = currentCategories.filter(c => c !== categoryLower);
      router.navigate({
        to: '/blog',
        search: {
          ...search,
          category: newCategories.length > 0 ? newCategories : undefined
        },
        replace: true
      });
    } else {
      router.navigate({
        to: '/blog',
        search: {
          ...search,
          category: [...currentCategories, categoryLower]
        },
        replace: true
      });
    }
    setCurrentPage(1);
  };

  const handleTagClick = (tag: string) => {
    router.navigate({
      to: '/blog',
      search: {
        ...search,
        tag: tag === tagFilter ? undefined : tag.toLowerCase()
      },
      replace: true
    });
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    router.navigate({
      to: '/blog',
      search: {},
      replace: true
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="border rounded-lg overflow-hidden shadow-sm">
                <div className="h-36 bg-gray-200"></div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Blog"
        description="Explore our latest articles, tutorials, and insights about AI, data applications, and web development."
        keywords="blog, articles, tutorials, AI, data applications, web development"
        ogUrl="https://preswald.com/blog"
      />
      <LandingHeader />
      <div className="max-w-5xl mx-auto px-4 py-8 flex-1">
        <h1 className="text-4xl font-bold mb-8">Blog</h1>

        {/* Search and Filter Section */}
        <div className="mb-8">
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2 border rounded-lg pr-12 text-gray-900"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 border rounded text-gray-900"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title-asc">Title A-Z</option>
              <option value="title-desc">Title Z-A</option>
            </select>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryClick(category)}
                className={`px-3 py-1 rounded-full text-sm ${categoryFilters.includes(category.toLowerCase())
                  ? 'bg-black text-white'
                  : 'border border-gray-300 text-gray-900 hover:border-gray-400'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-36 object-cover"
              />
              <div className="p-4">
                <h2 className="text-lg font-semibold mb-2 text-gray-900">{post.title.replace(/['"`]/g, '')}</h2>
                <p className="text-xs text-gray-600 mb-2">
                  {post.avatar && (
                    <img
                      src={post.avatar}
                      alt={post.author}
                      className="w-4 h-4 rounded-full inline-block mr-2"
                    />
                  )}
                  <span className="font-medium text-gray-900">{post.author}</span> • {new Date(post.date).toLocaleDateString()} • {post.readingTime}
                </p>
                <p className="text-xs text-gray-700 mb-2">{post.description}</p>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <button
                      key={tag}
                      onClick={(e) => {
                        e.preventDefault();
                        handleTagClick(tag);
                      }}
                      className={`text-[10px] text-gray-500 hover:underline ${tagFilter.toLowerCase() === tag.toLowerCase() ? 'underline' : ''
                        }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Show message if no posts found */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">
              No posts found
              {categoryFilters.length > 0 && ` in categories "${categoryFilters.join('", "')}"`}
              {tagFilter && ` with tag "#${tagFilter}"`}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
            <button
              onClick={clearAllFilters}
              className="text-gray-900 hover:underline mt-4 inline-block"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs text-gray-900 border border-gray-300 rounded hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-gray-900">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs text-gray-900 border border-gray-300 rounded hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
      <LandingFooter />
    </div>
  );
} 